<template>
  <div>
    <b-loading is-full-page v-model="isLoading"></b-loading>
    <b-notification
        v-model="isError"
        type="is-danger"
        aria-close-label="Close notification"
        :closable="false">
        <b>Error fetching campaign!</b><br />Try refreshing the page. If the error persists check your network connection or let BM know.
    </b-notification>
    <Keypress key-event="keyup" :modifiers="['ctrlKey']" :key-code="83" :preventDefault="true" @success="save" />

    <!-- Sticky Top Bar with Breadcrumb -->
    <TopBar
      v-if="!isLoading && !isError"
      parentTitle="Partner Campaigns"
      :currentTitle="(campaign.partner_name || 'New Partner') + ' | ' + (campaign.campaign_name || 'New Campaign')"
      :backRoute="{ name: 'PartnerCampaignListView' }"
    >
      <FeedsStatusBadge :status="campaign.status" />
      <b-button outlined type="is-primary" size="is-small" icon-left="check" @click="saveAndClose">Save & Close</b-button>
      <b-button type="is-primary" size="is-small" icon-left="content-save" @click="save">Save</b-button>
    </TopBar>

    <div v-if="!isLoading && !isError" class="page-content">
      <!-- Page Header -->
      <div class="page-header">
        <div class="page-title-group">
          <h1 class="page-title">{{ campaign.partner_name || "New Partner" }} | {{ campaign.campaign_name || 'New Campaign' }}</h1>
          <p class="page-subtitle">Partner Campaign Editor</p>
        </div>
      </div>

      <!-- Top Grid: Basic Info + Timing -->
      <div class="campaign-grid">
        <FeedsCard title="Basic Info">
          <div class="form-group">
            <label class="form-label">Status</label>
            <b-select v-model="campaign.status" placeholder="Status" expanded @input="save">
              <option value="draft">draft</option>
              <option value="test">test</option>
              <option value="fix">fix</option>
              <option value="live">live</option>
            </b-select>
          </div>
          <div class="form-group">
            <label class="form-label">Partner Name</label>
            <b-input
              v-model="campaign.partner_name"
              placeholder="e.g. Cooking Monthly Magazine"
              @input="saveDebounced"
            />
          </div>
          <div class="form-group">
            <label class="form-label">Partner ID</label>
            <div class="input-with-action">
              <b-input
                v-model="campaign.partner_id"
                placeholder="e.g. cooking_monthly"
                @input="saveDebounced"
              />
              <b-button type="is-light" @click="generatePartnerId">Generate</b-button>
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">Campaign Name</label>
            <b-input
              v-model="campaign.campaign_name"
              placeholder="e.g. Summer 2025 Promo"
              @input="saveDebounced"
            />
          </div>
          <div class="form-group">
            <label class="form-label">Deep Link Path</label>
            <div class="input-with-prefix">
              <span class="input-prefix">campaign-</span>
              <b-input
                v-model="deepLinkSuffix"
                placeholder="e.g. cm2025"
                @input="saveDebounced"
              />
              <b-button type="is-light" @click="generateDeepLinkPath">Generate</b-button>
            </div>
          </div>
          <ShareLink
            v-model="campaign.share_link"
            :selectedPath="campaign.deep_link_path || ''"
            :destinationUrl="`https://feedsfeedsfeeds.com/${campaign.partner_id || ''}?campaign_id=${id}`"
            :linkName="`Campaign: ${campaign.campaign_name || 'Untitled'}`"
            @input="save"
          />
        </FeedsCard>

        <FeedsCard title="Campaign Timing">
          <div class="form-group">
            <label class="form-label">Start Date</label>
            <b-datetimepicker
              v-model="startDate"
              placeholder="Select start date..."
              icon="calendar-today"
              :icon-right="campaign.start_date ? 'close-circle' : ''"
              icon-right-clickable
              @icon-right-click="clearStartDate"
              @input="save"
            />
          </div>
          <div class="form-group">
            <label class="form-label">End Date</label>
            <b-datetimepicker
              v-model="endDate"
              placeholder="Select end date (optional)..."
              icon="calendar-today"
              :icon-right="campaign.end_date ? 'close-circle' : ''"
              icon-right-clickable
              @icon-right-click="clearEndDate"
              @input="save"
            />
          </div>
        </FeedsCard>
      </div>

      <!-- Paywall Customization -->
      <FeedsCard title="Paywall Customization" class="mt-5">
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Hero Image</label>
            <EditCampaignImage
              v-model="campaign.hero_image_url"
              storage-path="images/campaigns/hero"
              aspect-ratio="flexible"
              title="Select Hero Image"
              @input="save"
            />
          </div>
          <div class="form-group">
            <label class="form-label">Logo</label>
            <EditCampaignImage
              v-model="campaign.logo_url"
              storage-path="images/campaigns/logos"
              aspect-ratio="square"
              title="Select Logo"
              :support-transparency="true"
              @input="save"
            />
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">Badge Text</label>
          <b-input
            v-model="campaign.badge_text"
            placeholder="e.g. LIMITED TIME OFFER (optional)"
            @input="saveDebounced"
          />
        </div>
        <div class="form-group">
          <label class="form-label">Hero Title</label>
          <b-input
            v-model="campaign.hero_title"
            placeholder="e.g. BECOME A MEMBER AND..."
            @input="saveDebounced"
          />
        </div>
        <div class="form-group">
          <label class="form-label">Subtitle</label>
          <b-input
            v-model="campaign.subtitle"
            placeholder="e.g. Get 6 months free, then €25/year"
            @input="saveDebounced"
          />
        </div>
        <div class="form-group">
          <label class="form-label">Brand Color</label>
          <div class="color-input-row">
            <input
              type="color"
              v-model="campaign.brand_color_hex"
              class="color-picker"
              @input="saveDebounced"
            />
            <b-input
              v-model="campaign.brand_color_hex"
              placeholder="#FF5733"
              maxlength="7"
              style="max-width: 120px"
              @input="saveDebounced"
            />
            <span
              v-if="campaign.brand_color_hex"
              class="color-preview"
              :style="{ backgroundColor: campaign.brand_color_hex, color: getContrastColor(campaign.brand_color_hex) }"
            >
              Preview
            </span>
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">Benefits</label>
          <div class="benefits-list">
            <draggable
              v-model="campaign.benefits"
              draggable=".benefit-item"
              handle=".drag-handle"
              ghost-class="drag-ghost"
              drag-class="drag-item"
              @end="save">
              <div v-for="(benefit, index) in campaign.benefits" :key="index" class="benefit-item">
                <b-icon class="drag-handle" icon="drag" />
                <b-select v-model="benefit.icon" placeholder="Icon" @input="save">
                  <option value="star">Star</option>
                  <option value="heart">Heart</option>
                  <option value="calendar">Calendar</option>
                  <option value="check">Check</option>
                  <option value="food">Food</option>
                  <option value="cart">Cart</option>
                  <option value="clock">Clock</option>
                  <option value="account">Account</option>
                </b-select>
                <div class="benefit-editor">
                  <editor v-model="benefit.text" @input="saveDebounced" />
                </div>
                <b-button type="is-danger" icon-right="delete" size="is-small" @click="removeBenefit(index)" />
              </div>
            </draggable>
            <b-button type="is-primary" icon-left="plus" size="is-small" @click="addBenefit">
              Add Benefit
            </b-button>
          </div>
        </div>
      </FeedsCard>

      <!-- Welcome Modal -->
      <FeedsCard title="Welcome Modal" class="mt-5">
        <div class="form-group">
          <label class="form-label">Welcome Title</label>
          <div class="editor-with-clear">
            <editor v-model="campaign.welcome_title" @input="saveDebounced" />
            <b-button type="is-light" icon-right="close" size="is-small" @click="clearField('welcome_title')" title="Clear" />
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">Welcome Message</label>
          <div class="editor-with-clear">
            <editor v-model="campaign.welcome_message" @input="saveDebounced" />
            <b-button type="is-light" icon-right="close" size="is-small" @click="clearField('welcome_message')" title="Clear" />
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">Welcome Image</label>
          <EditCampaignImage
            v-model="campaign.welcome_image_url"
            storage-path="images/campaigns/welcome"
            aspect-ratio="flexible"
            title="Select Welcome Image"
            @input="save"
          />
        </div>
      </FeedsCard>

      <!-- RevenueCat Integration -->
      <FeedsCard title="RevenueCat Integration" class="mt-5">
        <div class="form-group">
          <label class="form-label">Offering ID</label>
          <b-input
            v-model="campaign.offering_id"
            placeholder="e.g. partner_cooking_monthly_6mo"
            @input="saveDebounced"
          />
          <p class="form-hint">Create this offering in the RevenueCat dashboard first. The offering ID must match exactly.</p>
        </div>
      </FeedsCard>

      <!-- Preview -->
      <FeedsCard title="Campaign Preview" class="mt-5">
        <div class="preview-card" :style="{ borderColor: campaign.brand_color_hex }">
          <div v-if="campaign.hero_image_url" class="preview-hero">
            <img :src="campaign.hero_image_url" />
          </div>
          <div class="preview-content">
            <div v-if="campaign.logo_url" class="preview-logo-row">
              <img :src="campaign.logo_url" class="preview-logo" />
              <p class="preview-title">{{ campaign.hero_title }}</p>
            </div>
            <p v-else class="preview-title">{{ campaign.hero_title }}</p>
            <p class="preview-subtitle">{{ campaign.subtitle }}</p>
            <ul class="preview-benefits">
              <li v-for="(benefit, index) in campaign.benefits" :key="index">
                <b-icon :icon="benefit.icon" size="is-small" />
                <span v-html="benefit.text"></span>
              </li>
            </ul>
          </div>
        </div>
      </FeedsCard>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import db from "@/firebase/db"
import Draggable from 'vuedraggable'
import EditCampaignImage from "@/components/EditCampaignImage.vue"
import Editor from "@/components/BaseEditor"
import FeedsCard from "@/components/common/FeedsCard"
import FeedsStatusBadge from "@/components/common/FeedsStatusBadge"
import ShareLink from "@/components/common/ShareLink"
import TopBar from "@/components/layout/TopBar"

export default {
  name: 'PartnerCampaignView',
  components: {
    Keypress: () => import('vue-keypress'),
    Draggable,
    EditCampaignImage,
    Editor,
    FeedsCard,
    FeedsStatusBadge,
    ShareLink,
    TopBar
  },
  props: ['id'],
  data() {
    return {
      campaign: {
        benefits: []
      },
      isLoading: true,
      isError: false
    }
  },
  computed: {
    startDate: {
      get() {
        if (!this.campaign.start_date) return null
        return this.campaign.start_date.toDate ? this.campaign.start_date.toDate() : new Date(this.campaign.start_date)
      },
      set(val) {
        this.campaign.start_date = val
      }
    },
    endDate: {
      get() {
        if (!this.campaign.end_date) return null
        return this.campaign.end_date.toDate ? this.campaign.end_date.toDate() : new Date(this.campaign.end_date)
      },
      set(val) {
        this.campaign.end_date = val
      }
    },
    deepLinkSuffix: {
      get() {
        if (!this.campaign.deep_link_path) return ''
        const prefix = 'campaign-'
        if (this.campaign.deep_link_path.startsWith(prefix)) {
          return this.campaign.deep_link_path.substring(prefix.length)
        }
        return this.campaign.deep_link_path
      },
      set(val) {
        const cleanVal = val.replace(/^campaign-/, '')
        this.campaign.deep_link_path = 'campaign-' + cleanVal
      }
    }
  },
  created() {
    db.collection('partner_campaigns')
      .doc(this.id)
      .get()
      .then(snapshot => {
        if (snapshot.exists) {
          this.campaign = snapshot.data()
          if (!this.campaign.benefits) {
            this.campaign.benefits = []
          }
          if (this.campaign.priority == null) {
            this.campaign.priority = 10
          }
          if (this.campaign.deep_link_path && !this.campaign.deep_link_path.startsWith('campaign-')) {
            this.campaign.deep_link_path = 'campaign-' + this.campaign.deep_link_path
          }
        } else {
          this.isError = true
        }
        this.isLoading = false
      })
      .catch((error) => {
        this.isError = true
        this.$buefy.notification.open({ type: 'is-danger', message: `Error fetching campaign: ${error}` })
      })
  },
  methods: {
    saveDebounced: debounce(function() {
      this.save()
    }, 1000),
    async saveAndClose() {
      await this.save()
      this.$router.go(-1)
    },
    async save() {
      this.campaign.updated_at = new Date()
      return db.collection('partner_campaigns')
        .doc(this.id)
        .set(this.campaign)
        .then(() => {
          this.$buefy.toast.open({ type: 'is-success', message: 'Campaign successfully updated!' })
        })
        .catch((error) => {
          this.$buefy.toast.open({ type: 'is-danger', message: `Error saving campaign: ${error}` })
        })
    },
    generatePartnerId() {
      if (this.campaign.partner_name) {
        this.campaign.partner_id = this.campaign.partner_name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_|_$/g, '')
        this.save()
      }
    },
    generateDeepLinkPath() {
      if (this.campaign.campaign_name) {
        const suffix = this.campaign.campaign_name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')
        this.campaign.deep_link_path = 'campaign-' + suffix
        this.save()
      }
    },
    clearStartDate() {
      this.campaign.start_date = null
      this.save()
    },
    clearEndDate() {
      this.campaign.end_date = null
      this.save()
    },
    addBenefit() {
      if (!this.campaign.benefits) {
        this.campaign.benefits = []
      }
      this.campaign.benefits.push({ text: '', icon: 'star' })
    },
    removeBenefit(index) {
      this.$delete(this.campaign.benefits, index)
      this.save()
    },
    getContrastColor(hexColor) {
      if (!hexColor) return '#000000'
      const hex = hexColor.replace('#', '')
      const r = parseInt(hex.substr(0, 2), 16)
      const g = parseInt(hex.substr(2, 2), 16)
      const b = parseInt(hex.substr(4, 2), 16)
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
      return luminance > 0.5 ? '#000000' : '#ffffff'
    },
    clearField(fieldName) {
      this.campaign[fieldName] = ''
      this.save()
    },
  }
}
</script>

<style scoped>
.campaign-grid {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 24px;
  align-items: start;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.input-with-action {
  display: flex;
  gap: 8px;
}

.input-with-action .control {
  flex: 1;
}

.input-with-prefix {
  display: flex;
  align-items: center;
  gap: 8px;
}

.input-with-prefix .control {
  flex: 1;
}

.input-prefix {
  padding: 8px 12px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-sm);
  font-size: 14px;
  color: var(--text-secondary);
}

.color-input-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.color-picker {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  padding: 0;
}

.color-preview {
  padding: 8px 16px;
  border-radius: var(--radius-sm);
  font-size: 14px;
  font-weight: 500;
}

.benefits-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.benefit-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.benefit-item .drag-handle {
  cursor: grab;
  color: var(--text-muted);
  padding-top: 10px;
}

.benefit-item .drag-handle:active {
  cursor: grabbing;
}

.benefit-editor {
  flex: 1;
  min-width: 0;
}

.editor-with-clear {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.editor-with-clear > div:first-child {
  flex: 1;
  min-width: 0;
}

.preview-card {
  max-width: 400px;
  border: 3px solid var(--border-medium);
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: var(--bg-secondary);
}

.preview-hero {
  width: 100%;
  aspect-ratio: 16/9;
  overflow: hidden;
}

.preview-hero img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-content {
  padding: 20px;
}

.preview-logo-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.preview-logo {
  width: 48px;
  height: 48px;
  object-fit: contain;
}

.preview-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.preview-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 8px 0 16px;
}

.preview-benefits {
  list-style: none;
  padding: 0;
  margin: 0;
}

.preview-benefits li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  font-size: 14px;
  color: var(--text-primary);
}

.drag-ghost {
  opacity: 0.5;
  background: var(--bg-tertiary);
}
</style>
